import React, { useContext, useEffect, useMemo, useState } from "react";
import { Modal, Spinner } from "reactstrap";
import { FiX } from "react-icons/fi";
import axios from "axios";
import { Language } from "../App";
import { adminBaseURL, ReportCategoriesApi } from "../constants/API";
import MixpanelManager from "../constants/MixpanelManager";
import { MixpanelEvents } from "../constants/MixpanelEvents";
import "./ReportModal.css";

const ReportModal = ({
  isOpen,
  show,
  toggle,
  onClose,
  handleReport,
  onSubmit,
  onConfirm,
  isLoading,
  loading,
  heading,
  title,
  type = "event",
  reportType,
}) => {
  const { lang } = useContext(Language) || {};
  const selectedLanguage = localStorage.getItem("selectedLanguage") || "English";
  const isSpanish = selectedLanguage === "Spanish";

  const isModalOpen = Boolean(isOpen ?? show);
  const isSubmitting = Boolean(isLoading ?? loading);

  // Close handler supporting multiple signature types
  const handleClose = () => {
    if (typeof toggle === "function") {
      toggle(false);
    } else if (typeof onClose === "function") {
      onClose();
    }
  };

  // Submit callback
  const submitCallback = handleReport || onSubmit || onConfirm;

  // Contextual item calculation
  const normalizedType = String(reportType || type || "").toLowerCase();
  const headingStr = String(heading || title || "").toLowerCase();

  const isWebinar = normalizedType.includes("webinar") || headingStr.includes("webinar");
  const isSession =
    normalizedType.includes("session") ||
    normalizedType.includes("clinical") ||
    headingStr.includes("session") ||
    headingStr.includes("clinical");
  const isPost = normalizedType.includes("post") || headingStr.includes("post");
  const isCourse = normalizedType.includes("course") || headingStr.includes("course");

  // Determine Title
  const modalTitle =
    heading ||
    title ||
    (isWebinar
      ? lang?.report_webinar || "Report this webinar"
      : isSession
      ? lang?.report_clinical_session || "Report this session"
      : isPost
      ? lang?.report_post || "Report this post"
      : isCourse
      ? lang?.report_course || "Report this course"
      : lang?.report_event || "Report this event");

  // Determine Subtitle
  const modalSubtitle =
    lang?.report_confidential_desc ||
    lang?.tell_us_whats_wrong ||
    "Tell us what's wrong. Reports are confidential.";

  // Contextual fourth option
  const contextualOption = useMemo(() => {
    if (isWebinar) return lang?.report_reason_fake_webinar || "Not a real medical webinar";
    if (isSession) return lang?.report_reason_fake_session || "Not a real clinical session";
    if (isPost) return lang?.report_reason_fake_post || "Not a real medical post";
    if (isCourse) return lang?.report_reason_fake_course || "Not a real medical course";
    return lang?.report_reason_fake_event || "Not a real medical event";
  }, [isWebinar, isSession, isPost, isCourse, lang]);

  // Default fallback categories if API categories are loading or unavailable
  const defaultCategories = useMemo(
    () => [
      { id: "misleading", name: "Misleading or false information", spanish_name: lang?.report_reason_misleading },
      { id: "spam", name: "Spam or scam", spanish_name: lang?.report_reason_spam },
      { id: "inappropriate", name: "Inappropriate or offensive content", spanish_name: lang?.report_reason_inappropriate },
      { id: "contextual", name: contextualOption, spanish_name: contextualOption },
      { id: "other", name: "Something else", spanish_name: lang?.report_reason_something_else },
    ],
    [contextualOption, lang]
  );

  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [details, setDetails] = useState("");

  const displayedCategories = categories.length > 0 ? categories : defaultCategories;

  // Fetch report categories from backend API
  useEffect(() => {
    let isMounted = true;
    const fetchCategories = async () => {
      try {
        setCategoriesLoading(true);
        const categoriesUrl = ReportCategoriesApi ;
        const res = await axios.get(categoriesUrl);
        if (isMounted && res?.data?.status && Array.isArray(res?.data?.data)) {
          setCategories(res.data.data);
          if (res.data.data.length > 0) {
            setSelectedCategory(res.data.data[0]);
          }
        }
      } catch (err) {
        console.error("Error fetching report categories:", err);
      } finally {
        if (isMounted) {
          setCategoriesLoading(false);
        }
      }
    };

    fetchCategories();

    return () => {
      isMounted = false;
    };
  }, []);

  // Reset state when modal opens
  useEffect(() => {
    if (isModalOpen) {
      setDetails("");
      if (categories.length > 0) {
        setSelectedCategory(categories[0]);
      } else if (defaultCategories.length > 0) {
        setSelectedCategory(defaultCategories[0]);
      }
    }
  }, [isModalOpen, categories, defaultCategories]);

  const handleSubmit = () => {
    const trimmedDetails = details.trim();
    const activeCategory = selectedCategory || displayedCategories[0];
    const categoryName =
      typeof activeCategory === "object"
        ? activeCategory?.name || ""
        : String(activeCategory || "");
    const categoryId =
      typeof activeCategory === "object" ? activeCategory?.id : null;

    // Report param receives the textarea text; category param receives the selected category
    const reportPayload = {
      category: categoryName,
      report: trimmedDetails,
      category_id: categoryId,
    };

    try {
      if (MixpanelManager?.track && MixpanelEvents?.courseReported) {
        MixpanelManager.track(MixpanelEvents.courseReported, {
          category: categoryName,
          report: trimmedDetails,
          heading: modalTitle,
        });
      }
    } catch (e) {
      // ignore mixpanel tracking failures
    }

    if (typeof submitCallback === "function") {
      submitCallback(reportPayload, categoryName);
    }
  };

  return (
    <Modal
      isOpen={isModalOpen}
      toggle={handleClose}
      centered
      dialogClassName="report-modal-dialog"
      contentClassName="report-modal-content"
      fade={true}
      zIndex={9999}
      backdrop="static"
    >
      <div className="report-modal-card">
        {/* Close Button */}
        <button
          type="button"
          className="report-modal-close-btn"
          onClick={handleClose}
          aria-label="Close"
          disabled={isSubmitting}
        >
          <FiX size={14} />
        </button>

        {/* Header */}
        <div className="report-modal-header">
          <h2 className="report-modal-title">{modalTitle}</h2>
          <p className="report-modal-subtitle">{modalSubtitle}</p>
        </div>

        {/* Radio Options List */}
        <div className="report-modal-options">
          {categoriesLoading && categories.length === 0 ? (
            <div className="d-flex justify-content-center align-items-center py-4">
              <Spinner size="sm" color="primary" />
            </div>
          ) : (
            displayedCategories.map((option, index) => {
              const optionId = option?.id ?? index;
              const optionName = typeof option === "object" ? option.name : option;
              const selectedId = selectedCategory?.id;
              const selectedName =
                typeof selectedCategory === "object"
                  ? selectedCategory?.name
                  : selectedCategory;
              const isSelected =
                (selectedId !== undefined && selectedId !== null && selectedId === optionId) ||
                selectedName === optionName;

              const label =
                isSpanish && option?.spanish_name
                  ? option.spanish_name
                  : optionName || option;

              return (
                <button
                  key={optionId}
                  type="button"
                  className="report-modal-option-row"
                  onClick={() => setSelectedCategory(option)}
                >
                  <div className={`report-modal-radio-icon ${isSelected ? "active" : ""}`}>
                    {isSelected && <div className="report-modal-radio-dot" />}
                  </div>
                  <span className={`report-modal-option-label ${isSelected ? "active" : ""}`}>
                    {label}
                  </span>
                </button>
              );
            })
          )}
        </div>

        {/* Textarea Details (Group 1000002351 & Frame 1686558070) */}
        <div className="report-modal-details-wrap">
          <textarea
            className="report-modal-textarea"
            placeholder={lang?.report_add_details_optional || "Add more details (optional)"}
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            disabled={isSubmitting}
          />
        </div>

        {/* Action Buttons (Frame 2147232891) */}
        <div className="report-modal-actions">
          {/* Cancel (Frame 2147232889) */}
          <button
            type="button"
            className="report-modal-cancel-btn"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            {lang?.cancel || "Cancel"}
          </button>

          {/* Submit Report (Frame 2147232890) */}
          <button
            type="button"
            className="report-modal-submit-btn"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <Spinner size="sm" color="light" />
            ) : (
              lang?.submit_report || "Submit Report"
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ReportModal;
